import { useMemo } from 'react';
import clsx from 'clsx';

import { DEFAULT_USAGE_PRECISION } from '../../../../constants';
import { AggregationType, Granularity, MeterType, type UsageSeriesList } from '../../../../types';
import { Chart } from '../Chart';
import { getUsageChartType, type UsageChartType } from '../usageChartPrimitives';

import type { UsageTooltipComponent } from './getTooltipFormatter';
import { getUsageChartOptions } from './getUsageChartOptions';
import {
  addContextToSeries,
  calculateAverageUsage,
  getMonthlyUsageResetTimestamps,
  getNormalizedSeries,
} from './utils';

import styles from './UsageDetailChart.module.css';

type UsageDetailChartProps = {
  data: UsageSeriesList;
  aggregationType: AggregationType;
  // The abbreviated metric name.
  abbreviatedName: string;
  // The start timestamp for the chart, inclusive.
  fromTimestampUTC: number;
  // The end timestamp for the chart, exclusive.
  toTimestampUTC: number;
  // Include a cutoff timestamp for today with cumulative data.
  cutoffTimestampUTC: number;
  // The metric limit value.
  limitValue?: number;
  // The maximum y-axis value.
  yAxisMax?: number;
  // If the metric is a sum or unique meter.
  meterType?: MeterType;
  // The height of the chart.
  size?: 'small' | 'large';
  // The precision of the chart.
  precision?: number;
  // The metadata index per groupBy result.
  metaDataIdx?: number;
  // If true, labels will be shortened to save space.
  condenseLabels?: boolean;
  // The granularity of the chart.
  granularity?: Granularity;
  // Override the config-derived chart type while retaining the shared chart implementation.
  chartType?: UsageChartType;
  // If true, marklines will be hidden.
  hideMarklines?: boolean;
  // The trial end timestamp for showing trial end marker.
  trialEndUTC?: number;
  // Optional name displayed on the single value axis.
  yAxisName?: string;
  // Optional name for the visible total series.
  seriesName?: string;
  // Component values shown as declarative tooltip detail rows.
  tooltipComponents?: readonly UsageTooltipComponent[];
  seriesColor?: string;
  // Draw the dashed month-end projection line. Caller-owned (see `useSuspenseUsageData.hasProjection`);
  // only the aggregate total chart (ungrouped, metaDataIdx 0) sets it, not breakdown charts.
  showProjection?: boolean;
};

export function UsageDetailChart({
  data,
  aggregationType,
  abbreviatedName,
  limitValue,
  fromTimestampUTC,
  toTimestampUTC,
  cutoffTimestampUTC,
  meterType,
  size = 'small',
  yAxisMax,
  precision = DEFAULT_USAGE_PRECISION,
  metaDataIdx = 0,
  condenseLabels = true,
  granularity = Granularity.DAILY,
  chartType: chartTypeOverride,
  hideMarklines = false,
  trialEndUTC,
  yAxisName,
  seriesName,
  tooltipComponents,
  seriesColor,
  showProjection = false,
}: UsageDetailChartProps) {
  const chartOptions = useMemo(() => {
    const chartType = chartTypeOverride ?? getUsageChartType(aggregationType, granularity);
    const hasAreaStyles = chartType === 'line' && aggregationType === AggregationType.MONTH_TO_DATE;
    const normalizedSeries = getNormalizedSeries(
      data.series,
      fromTimestampUTC,
      toTimestampUTC,
      cutoffTimestampUTC,
      aggregationType,
      metaDataIdx,
      granularity,
    );
    const showAvgMarkline =
      !hideMarklines &&
      (aggregationType === AggregationType.AVERAGE ||
        (aggregationType === AggregationType.INCREMENTAL && meterType !== MeterType.UNIQUE));
    const avgUsage = showAvgMarkline ? calculateAverageUsage(normalizedSeries) : undefined;
    const usageResets = hasAreaStyles && !hideMarklines ? getMonthlyUsageResetTimestamps(normalizedSeries) : [];
    const visibleLimitValue = hasAreaStyles && !hideMarklines ? limitValue : undefined;
    const seriesWithContext = data.primaryContextKindByTimestamp
      ? addContextToSeries(normalizedSeries, data.primaryContextKindByTimestamp)
      : normalizedSeries;
    const showSymbol = hasAreaStyles && normalizedSeries.filter((point) => point.value !== null).length === 1;

    const validTrialEnd =
      !hideMarklines && trialEndUTC && trialEndUTC > fromTimestampUTC && trialEndUTC < toTimestampUTC;
    const visibleTrialEnd = validTrialEnd ? trialEndUTC : undefined;

    // The projection only carries the ungrouped '0' dimension, so require metaDataIdx === 0 (on top
    // of the caller's showProjection gate) so a breakdown chart can never draw a misleading zero line.
    const projectedSeries =
      showProjection && aggregationType === AggregationType.MONTH_TO_DATE && metaDataIdx === 0 && data.projectedSeries
        ? data.projectedSeries.map((slice) => ({
            time: slice.time,
            value: slice[metaDataIdx.toString()] ?? null,
          }))
        : undefined;

    return getUsageChartOptions({
      series: seriesWithContext,
      chartType,
      abbreviatedName,
      meterType,
      limitValue: visibleLimitValue,
      avgUsage,
      hasAreaStyles,
      condenseLabels,
      precision,
      yAxisMax,
      usageResets,
      granularity,
      showSymbol,
      trialEndUTC: visibleTrialEnd,
      yAxisName,
      seriesName,
      tooltipComponents,
      projectedSeries,
      seriesColor,
    });
  }, [
    data,
    abbreviatedName,
    limitValue,
    fromTimestampUTC,
    toTimestampUTC,
    cutoffTimestampUTC,
    aggregationType,
    meterType,
    condenseLabels,
    precision,
    metaDataIdx,
    yAxisMax,
    granularity,
    chartTypeOverride,
    hideMarklines,
    trialEndUTC,
    yAxisName,
    seriesName,
    tooltipComponents,
    seriesColor,
    showProjection,
  ]);

  return (
    <div
      className={clsx(
        styles.chartContainer,
        size === 'small' ? styles.chartContainerSmall : styles.chartContainerLarge,
      )}
    >
      <Chart option={chartOptions} opts={{ renderer: 'svg' }} style={{ width: '100%', height: '100%' }} notMerge />
    </div>
  );
}
